import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import { useToast } from '../../context/ToastContext.jsx';
import Icon from '../ui/Icon.jsx';
import './UserMenu.css';

/**
 * Header 右上角的會員選單。
 *
 * - 未登入：顯示 user icon，連到 /login
 * - 已登入：顯示縮寫頭像，點擊展開下拉（會員中心 / 訂單 / 登出）
 */
function UserMenu() {
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  // 點選單外面時自動關閉
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  if (!isAuthenticated) {
    return (
      <Link className="icon-btn" to="/login" aria-label="會員登入">
        <Icon name="user" size={26} />
      </Link>
    );
  }

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    showToast('已登出', 'info', 2500);
    navigate('/');
  };

  return (
    <div className="user-menu" ref={ref}>
      <button
        className="user-menu-trigger"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="會員選單"
      >
        <span className="user-menu-avatar">{user.initial || user.name?.[0] || '?'}</span>
      </button>

      {open && (
        <div className="user-menu-dropdown" role="menu">
          <div className="user-menu-head">
            <span className="user-menu-avatar large">{user.initial || user.name?.[0] || '?'}</span>
            <div>
              <b>{user.name}</b>
              <small>{user.email}</small>
              {isAdmin
                ? <span className="user-menu-tier admin-tier">後台</span>
                : user.tier && <span className="user-menu-tier">{user.tier}</span>}
            </div>
          </div>
          <div className="user-menu-divider"></div>

          {/* 有後台權限者多一個後台入口；會員功能所有登入者都看得到 */}
          {isAdmin && (
            <Link to="/admin" className="user-menu-item admin" onClick={() => setOpen(false)} role="menuitem">
              ⚙ 後台管理
            </Link>
          )}
          <Link to="/account" className="user-menu-item" onClick={() => setOpen(false)} role="menuitem">
            會員中心
          </Link>
          <Link to="/account?tab=orders" className="user-menu-item" onClick={() => setOpen(false)} role="menuitem">
            我的訂單
          </Link>

          <div className="user-menu-divider"></div>
          <button className="user-menu-item danger" onClick={handleLogout} role="menuitem">
            登出
          </button>
        </div>
      )}
    </div>
  );
}

export default UserMenu;
